import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Plus, 
  Trash2, 
  Eye,
  Edit3
} from 'lucide-react';
import { howItWorksAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import {
  HowItWorksContainer,
  SectionHeader,
  Title,
  Subtitle,
  Form,
  FormGroup,
  Label,
  Input,
  TextArea,
  StepsContainer,
  StepCard,
  StepHeader,
  StepTitle,
  RemoveStepButton,
  ActionButton,
  SubmitButton,
  LoadingSpinner,
  ErrorMessage,
  PreviewSection,
  PreviewTitle,
  PreviewContent,
  PreviewHeading,
  PreviewExplanation,
  StepsPreview,
  StepPreviewCard,
  StepNumber,
  StepPreviewTitle,
  StepPreviewBrief,
  StepPreviewDetailed,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText
} from './HowItWorksSection.styles';

const HowItWorksSection = () => {
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    heading: '',
    explanation: '',
    steps: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingData, setExistingData] = useState(null);

  useEffect(() => {
    loadHowItWorks();
  }, []);

  const loadHowItWorks = async () => {
    try {
      setLoading(true);
      const response = await howItWorksAPI.get();
      if (response.status === 200) {
        const data = response.data;
        setFormData({
          heading: data.heading || '',
          explanation: data.explanation || '',
          steps: data.steps || []
        });
        setExistingData(data);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        const errorMessage = error.response?.data?.message || 'Failed to load how it works data';
        showError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, {
        title: '',
        brief_paragraph: '',
        detailed_paragraph: ''
      }]
    }));
  };

  const removeStep = (index) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const handleStepChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.heading.trim()) {
      setError('Heading is required');
      return;
    }

    if (!formData.explanation.trim()) {
      setError('Explanation is required');
      return;
    }

    if (formData.steps.length === 0) {
      setError('At least one step is required');
      return;
    }

    // Validate each step
    for (let i = 0; i < formData.steps.length; i++) {
      const step = formData.steps[i];
      if (!step.title.trim() || !step.brief_paragraph.trim() || !step.detailed_paragraph.trim()) {
        setError(`Step ${i + 1}: All fields are required`);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const response = existingData 
        ? await howItWorksAPI.update(formData)
        : await howItWorksAPI.create(formData);

      if (response.status === 200 || response.status === 201) {
        showSuccess(existingData ? 'How it works section updated successfully!' : 'How it works section created successfully!');
        setExistingData(response.data);
        loadHowItWorks(); // Reload to get updated data
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save how it works section';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !existingData) {
    return (
      <HowItWorksContainer>
        <SectionHeader>
          <Title>How It Works Section</Title>
          <Subtitle>Loading...</Subtitle>
        </SectionHeader>
      </HowItWorksContainer>
    );
  }

  return (
    <HowItWorksContainer>
      <SectionHeader>
        <Title>How It Works Section Management</Title>
        <Subtitle>Manage your website's how it works section with heading, explanation, and step-by-step process</Subtitle>
      </SectionHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="heading">Heading *</Label>
          <Input
            id="heading"
            type="text"
            value={formData.heading}
            onChange={(e) => handleInputChange('heading', e.target.value)}
            placeholder="e.g., How It Works"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="explanation">Explanation *</Label>
          <TextArea
            id="explanation"
            value={formData.explanation}
            onChange={(e) => handleInputChange('explanation', e.target.value)}
            placeholder="Brief explanation of the process..."
            required
          />
        </FormGroup>

        <StepsContainer>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <Label>Steps *</Label>
            <ActionButton type="button" onClick={addStep}>
              <Plus size={16} />
              Add Step
            </ActionButton>
          </div>

          {formData.steps.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>📋</EmptyStateIcon>
              <EmptyStateText>No steps added yet. Click "Add Step" to get started.</EmptyStateText>
            </EmptyState>
          ) : (
            formData.steps.map((step, index) => (
              <StepCard key={index}>
                <StepHeader>
                  <StepTitle>Step {index + 1}</StepTitle>
                  <RemoveStepButton type="button" onClick={() => removeStep(index)}>
                    <Trash2 size={14} />
                    Remove
                  </RemoveStepButton>
                </StepHeader>

                <FormGroup>
                  <Label htmlFor={`step-${index}-title`}>Step Title *</Label>
                  <Input
                    id={`step-${index}-title`}
                    type="text"
                    value={step.title}
                    onChange={(e) => handleStepChange(index, 'title', e.target.value)}
                    placeholder="e.g., Step 1: Sign Up"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor={`step-${index}-brief`}>Brief Paragraph *</Label>
                  <TextArea
                    id={`step-${index}-brief`}
                    value={step.brief_paragraph}
                    onChange={(e) => handleStepChange(index, 'brief_paragraph', e.target.value)}
                    placeholder="Brief description of this step..."
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor={`step-${index}-detailed`}>Detailed Paragraph *</Label>
                  <TextArea
                    id={`step-${index}-detailed`}
                    value={step.detailed_paragraph}
                    onChange={(e) => handleStepChange(index, 'detailed_paragraph', e.target.value)}
                    placeholder="Detailed explanation of this step..."
                    required
                    style={{ minHeight: '120px' }}
                  />
                </FormGroup>
              </StepCard>
            ))
          )}
          
          {formData.steps.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
              <ActionButton type="button" onClick={addStep}>
                <Plus size={16} />
                Add Another Step
              </ActionButton>
            </div>
          )}
        </StepsContainer>

        <SubmitButton type="submit" disabled={loading}>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <Save size={20} />
              {existingData ? 'Update How It Works Section' : 'Create How It Works Section'}
            </>
          )}
        </SubmitButton>
      </Form>

      {existingData && (
        <PreviewSection>
          <PreviewTitle>
            <Eye size={20} />
            Preview
          </PreviewTitle>
          <PreviewContent>
            <PreviewHeading>{existingData.heading}</PreviewHeading>
            <PreviewExplanation>{existingData.explanation}</PreviewExplanation>
            
            {existingData.steps && existingData.steps.length > 0 && (
              <StepsPreview>
                {existingData.steps.map((step, index) => (
                  <StepPreviewCard key={index}>
                    <StepNumber>{index + 1}</StepNumber>
                    <StepPreviewTitle>{step.title}</StepPreviewTitle>
                    <StepPreviewBrief>{step.brief_paragraph}</StepPreviewBrief>
                    <StepPreviewDetailed>{step.detailed_paragraph}</StepPreviewDetailed>
                  </StepPreviewCard>
                ))}
              </StepsPreview>
            )}
          </PreviewContent>
        </PreviewSection>
      )}

    </HowItWorksContainer>
  );
};

export default HowItWorksSection;
